// Trends block (node page): the pure helpers the three hourly sparklines draw with. No imports,
// so the frontend tests can compile it. The points come from the node's Reach block (trends).

export const TRENDS_TITLE = "Trends";
export const PACKET_SIGHTINGS_TITLE = "Packet Sightings";
export const SNR_DESCRIPTION = "Median of observers' average SNR for verified signed adverts heard directly (empty path).";

export function trendsCaption(windowKey: string): string {
  return `Selected ${windowKey} window. Each point is one hourly bucket; gaps mean no measurement, while zero is a measured zero. Counts include this repeater's appearances in packet paths and direct receptions across observers; the same transmission can count more than once.`;
}

// Whether a node's trends hold anything to draw: arrays present and at least one hour with a value.
export function trendsHasData(t: { hour_start?: string[]; packets?: (number | null)[]; observers?: (number | null)[]; snr_db?: (number | null)[] } | null | undefined): boolean {
  if (!t || !t.hour_start || t.hour_start.length === 0) return false;
  return [t.packets, t.observers, t.snr_db].some((a) => (a ?? []).some((v) => v !== null && v !== undefined));
}

export interface TrendStats {
  latest: number;
  latestIndex: number;
  min: number;
  max: number;
}

// Latest value (the newest hour that has one) and the window min and max. Blank hours (null) are
// skipped, never counted as zero. Null when the window has no value at all.
export function trendStats(values: (number | null)[]): TrendStats | null {
  let latest: number | null = null;
  let latestIndex = -1;
  let min = Infinity;
  let max = -Infinity;
  for (const [i, v] of values.entries()) {
    if (v === null || v === undefined || Number.isNaN(v)) continue;
    latest = v;
    latestIndex = i;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return latest === null ? null : { latest, latestIndex, min, max };
}

// "latest 318 · Oct 6, 11:00 MDT · min 12 · max 702"; "no data" when blank.
export function trendStatsLine(stats: TrendStats | null, hours: string[], fmt: (v: number) => string, timeZone?: string): string {
  if (!stats) return "no data";
  const hour = hours[stats.latestIndex];
  const when = hour ? trendHourLabel(hour, timeZone) : "time unavailable";
  return `latest ${fmt(stats.latest)} · ${when} · min ${fmt(stats.min)} · max ${fmt(stats.max)}`;
}

// The Matt colour bands for SNR: green at or above -2 dB, yellow from -2 down to -7, red below -7.
export const SNR_BAND_GOOD_MIN = -2;
export const SNR_BAND_WARN_MIN = -7;
export type SnrBand = "good" | "warn" | "bad";

export function snrBand(db: number): SnrBand {
  if (db >= SNR_BAND_GOOD_MIN) return "good";
  if (db >= SNR_BAND_WARN_MIN) return "warn";
  return "bad";
}

// The colour token of a band (global.css, the rf good / warn / bad tokens).
export function snrBandColorVar(band: SnrBand): string {
  return band === "good" ? "--rf-good" : band === "warn" ? "--rf-warn" : "--rf-bad";
}

export interface SnrBandRect {
  band: SnrBand;
  /** Top and bottom of the band inside [lo, hi], in dB (to >= from). */
  from: number;
  to: number;
}

// The bands clipped to the chart range [lo, hi], top (best) first; bands outside the range are left out.
export function snrBandRects(lo: number, hi: number): SnrBandRect[] {
  const out: SnrBandRect[] = [];
  const edges: [SnrBand, number, number][] = [
    ["good", SNR_BAND_GOOD_MIN, Infinity],
    ["warn", SNR_BAND_WARN_MIN, SNR_BAND_GOOD_MIN],
    ["bad", -Infinity, SNR_BAND_WARN_MIN],
  ];
  for (const [band, bottom, top] of edges) {
    const from = Math.max(bottom, lo);
    const to = Math.min(top, hi);
    if (to > from) out.push({ band, from, to });
  }
  return out;
}

// SNR chart range: wide enough that every band shows, and every value fits.
export function snrRange(values: (number | null)[]): { lo: number; hi: number } {
  const s = trendStats(values);
  return { lo: Math.min(s ? s.min : 0, SNR_BAND_WARN_MIN - 2), hi: Math.max(s ? s.max : 0, SNR_BAND_GOOD_MIN + 2) };
}

// Line paths: one "M x y L x y ..." string per run of consecutive hours with data; a blank hour
// breaks the line. A single hour alone is drawn as a short flat stub so it stays visible.
export function trendLinePaths(values: (number | null)[], w: number, h: number, lo: number, hi: number, pad = 3): string[] {
  const n = values.length;
  const span = hi - lo || 1;
  const slot = (w - 2 * pad) / Math.max(n, 1);
  const x = (i: number) => pad + (i + 0.5) * slot;
  const y = (v: number) => h - pad - ((v - lo) / span) * (h - 2 * pad);
  const f = (v: number) => String(Math.round(v * 10) / 10);
  const paths: string[] = [];
  let run: string[] = [];
  const flush = (end: number) => {
    if (run.length === 1) run.push(`L${f(x(end - 1) + 1.5)} ${run[0].split(" ")[1]}`);
    if (run.length) paths.push(run.join(" "));
    run = [];
  };
  values.forEach((v, i) => {
    if (v === null || v === undefined) { flush(i); return; }
    run.push(`${run.length ? "L" : "M"}${f(x(i))} ${f(y(v))}`);
  });
  flush(n);
  return paths;
}

export interface TrendBar {
  index: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

// One bar per hour with data (a blank hour has no bar); the bars stand on the baseline, scaled to the window max.
export function trendBars(values: (number | null)[], w: number, h: number, pad = 3): TrendBar[] {
  const n = values.length;
  const max = Math.max(1, ...values.filter((v): v is number => v !== null && v !== undefined));
  const slot = (w - 2 * pad) / Math.max(n, 1);
  const width = Math.max(1, slot - (slot > 4 ? 1 : 0.5));
  const bars: TrendBar[] = [];
  values.forEach((v, i) => {
    if (v === null || v === undefined) return;
    const height = Math.max(1, (v / max) * (h - 2 * pad));
    bars.push({ index: i, x: pad + i * slot + (slot - width) / 2, y: h - pad - height, width, height });
  });
  return bars;
}

// The hour slot under the pointer, from its x within the plot (0 to w).
export function trendIndexAt(x: number, w: number, n: number): number {
  if (n <= 0) return -1;
  if (w <= 0) return 0;
  return Math.min(n - 1, Math.max(0, Math.floor((x / w) * n)));
}

// Step through every hour, including gaps so they can be announced as "no data".
export function trendIndexForKey(key: string, index: number, n: number): number | null {
  if (n <= 0) return null;
  if (key === "Home") return 0;
  if (key === "End") return n - 1;
  if (key === "ArrowLeft") return Math.max(0, index - 1);
  if (key === "ArrowRight") return Math.min(n - 1, index + 1);
  return null;
}

// Local browser time with its zone abbreviation, for example "Oct 6, 11:00 MDT".
export function trendHourLabel(iso: string, timeZone?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
      hourCycle: "h23", timeZoneName: "short", ...(timeZone ? { timeZone } : {}),
    }).format(d);
  } catch {
    return "";
  }
}

// Compact labels for the first and last hourly buckets shown beneath each chart.
export function trendEndpointLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "2-digit", hourCycle: "h23" }).format(d);
}

// The selected-hour readout; gaps are announced explicitly.
export function trendReadout(iso: string, value: number | null | undefined, fmt: (v: number) => string): string {
  return `${trendHourLabel(iso)} · ${value === null || value === undefined ? "no data" : fmt(value)}`;
}
